import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import {
  PageHeader,
  SectionHeader,
  Kpi,
  Card,
  RangeSelect,
  IncludeTestToggle,
  TestDataBanner,
  useIncludeTest,
  centsToUsd,
} from "./adminUi";

/** Gifts page — surfaces real Stripe/application gift data.
 *  Reuses `/admin/revenue` and `/admin/lifeguide-analytics` (both existing).
 *  No sensitive Stripe details are exposed.
 */
export default function AdminGiftsPage() {
  const [includeTest, setIncludeTest] = useIncludeTest();
  const [range, setRange] = useState("30d");
  const [rev, setRev] = useState(null);
  const [lg, setLg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setRev(null);
    setLg(null);
    Promise.all([
      adminApi.revenue({ range, include_test: includeTest }),
      adminApi.lifeguideAnalytics({ range }),
    ])
      .then(([r, l]) => {
        setRev(r);
        setLg(l);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load gifts"));
  }, [includeTest, range]);

  const g = rev?.gifts || {};
  const gi = lg?.gifts || {};
  const recent = rev?.recent_gifts || [];

  return (
    <div data-testid="admin-gifts-page" className="space-y-8">
      <PageHeader
        eyebrow="Programme"
        title="Gifts"
        subtitle="Every LifeGuide gift sold, redeemed, or scheduled — never raw card data, only aggregates."
        right={
          <div className="flex items-center gap-4">
            <RangeSelect value={range} onChange={setRange} />
            <IncludeTestToggle value={includeTest} onChange={setIncludeTest} />
          </div>
        }
      />
      <TestDataBanner show={includeTest} />

      {err && (
        <div className="rounded-2xl border border-[#B4795C]/50 bg-[#241914] text-[#F3D6A6] p-4 text-sm">
          {err}
        </div>
      )}

      <section>
        <SectionHeader label="Volume" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="gifts-purchased" label="Gifts purchased" value={g.purchased ?? gi.purchased} />
          <Kpi testid="gifts-redeemed" label="Gifts redeemed" value={g.redeemed ?? gi.redeemed} />
          <Kpi testid="gifts-scheduled" label="Gifts scheduled" value={g.scheduled ?? gi.scheduled} />
          <Kpi testid="gifts-revenue" label="Gift revenue" value={centsToUsd(g.revenue_cents)} tone="accent" />
          <Kpi testid="gifts-landing" label="Landing page visits" value={gi.landing_visits} />
          <Kpi
            testid="gifts-redemption-rate"
            label="Redemption rate"
            value={
              (g.purchased ?? gi.purchased) > 0
                ? `${Math.round(
                    (100 * (g.redeemed ?? gi.redeemed ?? 0)) / (g.purchased ?? gi.purchased)
                  )}%`
                : "—"
            }
          />
        </div>
      </section>

      <section>
        <SectionHeader label="Recent gifts" />
        <Card>
          {recent.length === 0 ? (
            <div className="text-sm text-[#8FA894] italic">No gifts yet in this range.</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {recent.slice(0, 20).map((r) => (
                <li
                  key={r.id}
                  data-testid={`gift-row-${r.id}`}
                  className="py-2.5 flex items-center justify-between gap-4 text-sm"
                >
                  <div className="min-w-0">
                    <div className="text-white truncate">
                      {r.duration_label || `${r.duration_days || 30}d gift`}
                    </div>
                    <div className="text-[11px] text-[#7C8F7C]">
                      {r.status} · {r.created_at?.slice(0, 10)}
                    </div>
                  </div>
                  <div className="tabular-nums text-[#B7D2AF]">
                    {centsToUsd(r.amount_cents)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>
    </div>
  );
}
